import { useEffect } from "react";
import { useApp } from "@/context/AppContext";

export default function SEO({
  title,
  description,
  keywords,
  canonical,
  lang: pageLang,
  faqItems = [],
  plans = [],
  robots = "index, follow",
  author = "AstaEnterprise",
  publisher = "AstaEnterprise",
  image = "https://astaenterprise.id/og-image.png",
  type = "website",
}) {
  const { lang: appLang } = useApp();
  const currentLang = pageLang || appLang || "id";

  const baseUrl = "https://astaenterprise.id";
  const canonicalUrl = canonical || (currentLang === "en" ? `${baseUrl}/en` : `${baseUrl}/`);

  useEffect(() => {
    // 1. Title & Description
    if (title) {
      document.title = title;
      setMetaName("title", title);
      setMetaProperty("og:title", title);
      setMetaName("twitter:title", title);
    }

    if (description) {
      setMetaName("description", description);
      setMetaProperty("og:description", description);
      setMetaName("twitter:description", description);
    }

    if (keywords) {
      setMetaName("keywords", keywords);
    }

    // 2. Canonical & Alternates (hreflang)
    setLinkRelation("canonical", canonicalUrl);
    setMetaProperty("og:url", canonicalUrl);
    setMetaProperty("twitter:url", canonicalUrl);

    setLinkAlternate("id", `${baseUrl}/`);
    setLinkAlternate("en", `${baseUrl}/en`);
    setLinkAlternate("x-default", `${baseUrl}/`);

    // 3. Robots & Author
    if (robots) setMetaName("robots", robots);
    if (author) setMetaName("author", author);
    if (publisher) setMetaName("publisher", publisher);

    // 4. OpenGraph & Twitter Cards
    setMetaProperty("og:type", type);
    setMetaProperty("og:site_name", "AstaEnterprise");
    setMetaProperty("og:locale", currentLang === "en" ? "en_US" : "id_ID");
    if (currentLang === "en") {
      setMetaProperty("og:locale:alternate", "id_ID");
    } else {
      setMetaProperty("og:locale:alternate", "en_US");
    }

    if (image) {
      setMetaProperty("og:image", image);
      setMetaProperty("og:image:width", "1200");
      setMetaProperty("og:image:height", "630");
      setMetaName("twitter:image", image);
      setMetaName("twitter:card", "summary_large_image");
    }

    // 5. Structured Data (Unified JSON-LD @graph)
    injectJsonLdGraph(currentLang, faqItems, plans, canonicalUrl);
  }, [title, description, keywords, canonicalUrl, currentLang, robots, author, publisher, image, type, faqItems, plans]);

  return null;
}

function setMetaName(name, content) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaProperty(property, content) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLinkRelation(rel, href) {
  let el = document.querySelector(`link[rel="${rel}"]:not([hreflang])`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setLinkAlternate(hreflang, href) {
  let el = document.querySelector(`link[rel="alternate"][hreflang="${hreflang}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "alternate");
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function injectJsonLdGraph(lang, faqItems = [], plans = [], currentUrl) {
  const isEn = lang === "en";
  const baseUrl = "https://astaenterprise.id";

  // Build dynamic offers from pricing plans
  let offers = {
    "@type": "Offer",
    "priceCurrency": "IDR",
    "price": "0",
    "availability": "https://schema.org/InStock",
    "priceValidUntil": "2027-12-31"
  };

  if (Array.isArray(plans) && plans.length > 0) {
    offers = plans.map((p) => {
      const numPrice = parseInt(String(p.price || "").replace(/[^0-9]/g, ""), 10) || 0;
      return {
        "@type": "Offer",
        "name": p.name,
        "description": p.tagline,
        "price": numPrice > 0 ? String(numPrice) : "0",
        "priceCurrency": "IDR",
        "availability": "https://schema.org/InStock",
        "priceValidUntil": "2027-12-31",
        ...(numPrice > 0
          ? {
              "priceSpecification": {
                "@type": "UnitPriceSpecification",
                "price": String(numPrice),
                "priceCurrency": "IDR",
                "unitCode": "MON"
              }
            }
          : {})
      };
    });
  }

  const defaultFaq = isEn
    ? [
        {
          q: "What is AstaEnterprise gold store software?",
          a: "AstaEnterprise is an integrated cloud ERP and POS system for jewelry businesses, featuring digital scale integration, karatage tracking, barcode tag printing, and automatic buyback calculations."
        },
        {
          q: "Does this software support official gold certificate and barcode printing?",
          a: "Yes, AstaEnterprise generates certified gold invoices with jewelry photos and prints barcode labels seamlessly."
        }
      ]
    : [
        {
          q: "Apa itu software toko emas AstaEnterprise?",
          a: "AstaEnterprise adalah aplikasi kasir POS dan sistem manajemen toko perhiasan emas yang mendukung integrasi timbangan presisi, pencatatan kadar emas/karat, cetak barcode tag, dan perhitungan buyback otomatis."
        },
        {
          q: "Apakah software toko emas ini mendukung cetak surat emas dan barcode?",
          a: "Ya, AstaEnterprise menyediakan fitur cetak nota/surat emas resmi dengan foto perhiasan serta cetak label barcode perhiasan secara otomatis."
        }
      ];

  const activeFaq = faqItems && faqItems.length > 0 ? faqItems : defaultFaq;

  // Unified @graph Schema
  const graphSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "SoftwareApplication",
        "@id": `${baseUrl}/#software`,
        "name": isEn
          ? "AstaEnterprise - Gold & Jewelry Store ERP & POS Software"
          : "AstaEnterprise - Software Toko Emas & POS Perhiasan",
        "operatingSystem": "Web, Windows, Android, iOS, macOS",
        "applicationCategory": "BusinessApplication",
        "description": isEn
          ? "Comprehensive jewelry store ERP and cloud POS application for karat tracking, precision digital scales, automated buybacks, barcode tag printing, and accounting."
          : "Software toko emas dan aplikasi kasir POS terpadu untuk pencatatan kadar emas, timbangan digital, transaksi buyback, cetak barcode tag perhiasan, dan pembukuan akuntansi.",
        "url": currentUrl || (isEn ? `${baseUrl}/en` : `${baseUrl}/`),
        "image": `${baseUrl}/og-image.png`,
        "offers": offers,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.9",
          "ratingCount": "200",
          "bestRating": "5",
          "worstRating": "1"
        },
        "publisher": {
          "@id": `${baseUrl}/#organization`
        }
      },
      {
        "@type": "Organization",
        "@id": `${baseUrl}/#organization`,
        "name": "AstaEnterprise",
        "url": `${baseUrl}/`,
        "logo": `${baseUrl}/favicon.png`,
        "sameAs": [
          "https://www.instagram.com/astaenterprise.id",
          "https://wa.me/6285183003076"
        ],
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+62-851-8300-3076",
          "contactType": "Customer Support",
          "areaServed": "ID",
          "availableLanguage": ["Indonesian", "English"]
        }
      },
      {
        "@type": "WebSite",
        "@id": `${baseUrl}/#website`,
        "name": "AstaEnterprise",
        "url": `${baseUrl}/`,
        "inLanguage": isEn ? "en-US" : "id-ID",
        "description": isEn
          ? "Modern ERP & POS Platform for Gold & Jewelry Stores in Indonesia"
          : "Sistem ERP & POS Bisnis Toko Emas & Berlian Modern di Indonesia",
        "publisher": {
          "@id": `${baseUrl}/#organization`
        }
      },
      {
        "@type": "FAQPage",
        "@id": `${baseUrl}/#faq`,
        "mainEntity": activeFaq.map((item) => ({
          "@type": "Question",
          "name": item.q,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.a
          }
        }))
      }
    ]
  };

  let graphScript = document.getElementById("structured-data-graph");
  if (!graphScript) {
    graphScript = document.createElement("script");
    graphScript.id = "structured-data-graph";
    graphScript.type = "application/ld+json";
    document.head.appendChild(graphScript);
  }
  graphScript.textContent = JSON.stringify(graphSchema);
}
